import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file DailyScheduleField.tsx
 * @description `dailySchedule` 欄位：多日「現場稽核日期 + 當日行程」渲染器（2026-09-25 回報序74）。
 *
 * 需求：
 *  1. 行程表不再每行填日期，行程繼承所屬組的「現場稽核日期」；
 *  2. 多日稽核：可新增多組「日期 + 當日行程」，每組各自獨立（可單獨刪除）；
 *  3. 「載入範例行程」：只預填當前組；當前組已有行程時先 Popconfirm 確認再覆蓋；
 *     載入後可任意修改、增刪行。
 *
 * 值形狀：Array<{ date: 'YYYY-MM-DD' | ''; rows: Record<colId, unknown>[] }>。
 * 非法形狀（含舊版扁平行陣列）以 normalizeDays 容錯：舊扁平行（有 date 欄）依日期分組。
 *
 * 當日行程表直接複用 TableField（行增刪、唯讀渲染一致），以合成的 table 欄位定義驅動。
 */
import { Button, DatePicker, Popconfirm, Space, Typography } from 'antd';
import { CalendarPlus, ListRestart, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { normalizeSiteScheduleDays } from '@zhcb/shared';
import { TableField } from './TableField';
/**
 * 把未知值規整為日組陣列。
 * - 新形狀（元素含 rows 陣列）：保留原樣（含空列，讓使用者正在編輯的空行不被吃掉）；
 * - 其他（舊扁平行等）：交給 shared 的 normalizeSiteScheduleDays 依日期分組。
 */
function normalizeDays(value) {
    if (!Array.isArray(value))
        return [];
    const isNewShape = value.some((v) => !!v && typeof v === 'object' && Array.isArray(v.rows));
    if (isNewShape) {
        return value
            .filter((v) => !!v && typeof v === 'object')
            .map((v) => ({
            date: typeof v.date === 'string' ? v.date : '',
            rows: Array.isArray(v.rows)
                ? v.rows.filter((r) => !!r && typeof r === 'object')
                : [],
        }));
    }
    return normalizeSiteScheduleDays(value).map((d) => ({
        date: d.date,
        rows: d.rows,
    }));
}
/** 當日行程是否已有任何非空內容（決定載入範例前是否需要確認） */
function dayHasContent(day) {
    return day.rows.some((r) => Object.values(r).some((v) => (typeof v === 'string' && v.trim() !== '') ||
        typeof v === 'number' ||
        typeof v === 'boolean'));
}
export const DailyScheduleField = memo(function DailyScheduleField({ field, value, onChange, disabled, uploadHandlers, }) {
    const days = normalizeDays(value);
    const maxDays = field.maxDays ?? 31;
    const dateLabel = field.dateLabel ?? '現場稽核日期';
    /** 合成的當日行程 table 欄位定義（各組共用，引用穩定） */
    const tableField = useMemo(() => ({
        id: `${field.id}__rows`,
        label: field.label,
        type: 'table',
        columns: field.columns,
        minRows: 1,
        maxRows: field.maxRowsPerDay ?? 30,
    }), [field]);
    /** 以不可變方式更新第 idx 組 */
    const updateDay = useCallback((idx, patch) => {
        onChange(normalizeDays(value).map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    }, [value, onChange]);
    const addDay = () => onChange([...days, { date: '', rows: [{}] }]);
    const removeDay = (idx) => onChange(days.filter((_, i) => i !== idx));
    /** 範例只預填當前組（深拷貝，避免多組共用同一物件引用） */
    const loadSample = (idx) => updateDay(idx, { rows: (field.sampleRows ?? []).map((r) => ({ ...r })) });
    return (_jsxs("div", { children: [days.length === 0 && (_jsx(Typography.Text, { type: "secondary", children: disabled ? '（無資料）' : '尚未安排稽核日，請點「新增稽核日」。' })), _jsx(Space, { direction: "vertical", size: 12, style: { width: '100%' }, children: days.map((day, idx) => (_jsxs("div", { style: {
                        border: '1px solid rgba(5, 5, 5, 0.1)',
                        borderRadius: 8,
                        padding: 12,
                    }, children: [_jsxs("div", { style: {
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 8,
                            }, children: [_jsxs(Typography.Text, { strong: true, children: ["\u7B2C ", idx + 1, " \u5929\u30FB", dateLabel, "\uFF1A"] }), disabled ? (_jsx(Typography.Text, { children: day.date || '—' })) : (_jsx(DatePicker, { size: "small", value: day.date ? dayjs(day.date) : null, onChange: (d) => updateDay(idx, { date: d ? d.format('YYYY-MM-DD') : '' }) })), !disabled && (_jsxs("span", { style: { marginLeft: 'auto', display: 'inline-flex', gap: 8 }, children: [field.sampleRows?.length ? (dayHasContent(day) ? (_jsx(Popconfirm, { title: "\u8F09\u5165\u7BC4\u4F8B\u884C\u7A0B", description: "\u672C\u65E5\u5DF2\u6709\u884C\u7A0B\u8CC7\u6599\uFF0C\u8F09\u5165\u7BC4\u4F8B\u5C07\u8986\u84CB\u672C\u65E5\u884C\u7A0B\uFF0C\u78BA\u5B9A\u8F09\u5165\uFF1F", okText: "\u8986\u84CB\u8F09\u5165", cancelText: "\u53D6\u6D88", onConfirm: () => loadSample(idx), children: _jsx(Button, { size: "small", icon: _jsx(ListRestart, { size: 13 }), children: "\u8F09\u5165\u7BC4\u4F8B\u884C\u7A0B" }) })) : (_jsx(Button, { size: "small", icon: _jsx(ListRestart, { size: 13 }), onClick: () => loadSample(idx), children: "\u8F09\u5165\u7BC4\u4F8B\u884C\u7A0B" }))) : null, _jsx(Popconfirm, { title: "\u522A\u9664\u6B64\u7A3D\u6838\u65E5", description: "\u5C07\u522A\u9664\u672C\u65E5\u671F\u53CA\u5176\u5168\u90E8\u884C\u7A0B\uFF0C\u78BA\u5B9A\uFF1F", okText: "\u522A\u9664", cancelText: "\u53D6\u6D88", onConfirm: () => removeDay(idx), children: _jsx(Button, { size: "small", danger: true, icon: _jsx(Trash2, { size: 13 }), children: "\u522A\u9664\u6B64\u65E5" }) })] }))] }), _jsx(TableField, { field: tableField, value: day.rows, onChange: (rows) => updateDay(idx, { rows: rows }), disabled: disabled, uploadHandlers: uploadHandlers })] }, idx))) }), !disabled && (_jsx(Button, { style: { marginTop: 12 }, icon: _jsx(CalendarPlus, { size: 14 }), disabled: days.length >= maxDays, onClick: addDay, children: "\u65B0\u589E\u7A3D\u6838\u65E5" }))] }));
});
//# sourceMappingURL=DailyScheduleField.js.map